import React, { useRef, useState } from 'react';
import Router from 'next/router';
import { confirmSignIn, resendSignUpCode, resetPassword } from 'aws-amplify/auth';
import { HStack, Link, Spinner, Text, VStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { toaster, Toaster } from '@/components/ui/toaster';
import { Button } from '@/components/ui/button';
import { Alert } from '@/components/ui/alert';

import { ValueChangeDetails } from '@zag-js/pin-input';
import { AuthStatus, useAuthDispatch, useAuthState } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useColorModeValue } from '../ui/color-mode';
import { PinInput } from '../ui/pin-input';
import { Field } from '../ui/field';

type VerifyCodeFormProps = {
  type?:
    | 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION'
    | 'CONTINUE_SIGN_IN_WITH_TOTP_SETUP'
    | 'CONFIRM_SIGN_IN_WITH_SMS_CODE'
    | 'CONFIRM_SIGN_IN_WITH_TOTP_CODE'
    | 'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
    | 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
    | 'CONFIRM_SIGN_UP';
  user?: {
    username: string;
    password: string;
  };
  callback?: () => void;
};

const VerifyCodeForm = ({ type, user, callback }: VerifyCodeFormProps) => {
  const DEFAULT_CODE = ['', '', '', '', '', ''];
  const { logEvent } = useAnalytics();
  const { signUpConfirm, dispatch } = useAuthDispatch();
  const { sessionStatus } = useAuthState();
  const inputRef = useRef(null);
  const [, setCode] = useState(DEFAULT_CODE);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation('auth');

  /*const handleCodeUpdate = ({ value }: ValueChangeDetails): void => {
    setCode(value);
  };*/

  const handleResendCode = async () => {
    setLoading(true);

    try {
      // Continue signup
      if (type === 'CONFIRM_SIGN_UP') {
        await resendSignUpCode({ username: user?.username || '' });
      }

      // Everything else
      if (type !== 'CONFIRM_SIGN_UP') {
        await resetPassword({ username: user?.username || '' });
      }

      setLoading(false);
      toaster.create({
        title: t('verify.resent'),
        description: t('verify.resentTo', { email: user?.username || '' }),
        type: 'success'
      });
    } catch (err: any) {
      setLoading(false);
      setError(err.message || t('verify.limitReached'));
    }
  };

  const handleCodeComplete = async ({ valueAsString }: ValueChangeDetails) => {
    setError('');
    setLoading(true);

    // Continue signup
    if (type === 'CONFIRM_SIGN_UP') {
      // TODO: Add option throwError?: boolean
      const resp = await signUpConfirm({
        code: valueAsString,
        email: user?.username
      }).catch((err) => {
        console.error({ action: 'onConfirmCode:Auth.confirmSignUp:error', error: err });
        setCode(DEFAULT_CODE);
        // TODO: Override errors in AuthContext
        // setError('Invalid code. Please use the latest email confirmation code and try again.');
        setLoading(false);
        // @ts-ignore
        inputRef.current?.focus();
      });

      // Let parent component know to Redirect to sign-in form
      // @ts-ignore
      if (resp?.nextStep?.signUpStep === 'DONE' && callback) {
        callback();
      }

      logEvent({
        action: 'signed_up_confirmed',
        category: 'Sign up confirmed'
      });

      return;
    }

    // MFA
    if (type === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
      // @ts-ignore
      const resp = await confirmSignIn({ challengeResponse: valueAsString }).catch((err) => {
        console.error({ action: 'onConfirmCode:Auth.confirmSignInTotp:error', error: err });
        dispatch({ type: 'signInTotpError', error: err });
        setCode(DEFAULT_CODE);
        // TODO: Override errors in AuthContext
        // setError('Invalid code. Please use the latest email confirmation code and try again.');
        setLoading(false);
        // @ts-ignore
        inputRef.current?.focus();
      });

      // Let parent component know to Redirect to sign-in form
      // @ts-ignore
      if (resp?.nextStep?.signUpStep === 'DONE' && callback) {
        callback();
      }

      logEvent({
        action: 'signed_in_totp',
        category: 'Signed in with TOTP'
      });

      return;
    }

    // Everything else
    const codeConfirmed = await confirmSignIn({ challengeResponse: valueAsString }).catch((err) => {
      console.error({ action: 'onConfirmCode:Auth.confirmSignIn:error', error: err });
      setCode(DEFAULT_CODE);
      setError(t('verify.invalidCode'));
      setLoading(false);
      // @ts-ignore
      inputRef.current?.focus();
    });

    // returned if not verified
    if (!codeConfirmed) {
      return;
    }

    // Route to account on successful login
    await Router.push('/dashboard/').catch((e) => {
      console.error({ action: 'onEmailVerified:route:error', error: e });
      setError(t('verify.loadError'));
    });
  };

  const handleTotpCancel = () => {
    dispatch({ type: 'anonymousSession' });
  };

  return (
    <VStack gap={6} alignItems='start'>
      {/* Errors */}
      {error.length > 0 && (
        <Alert status='error' colorPalette='brand' mb={6}>
          {error}
        </Alert>
      )}

      {/* Code */}
      <Field
        label={type !== 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' ? t('verify.emailCode') : t('verify.mfaCode')}
        invalid={!!error}
      >
        <HStack mb={6} gap={2} mt={6}>
          <PinInput
            otp
            autoFocus
            count={6}
            colorPalette='brand'
            size='md'
            disabled={loading} // Breaks autofocus
            invalid={!!error} // Interferes with other errors e.g. Resend pin limit
            onValueComplete={handleCodeComplete}
            // onValueChange={handleCodeUpdate}
          />
          <Spinner
            display={loading || sessionStatus === AuthStatus.PENDING_SIGN_UP_CONFIRM ? 'block' : 'none'}
            color='brand.300'
          />
        </HStack>
      </Field>

      {type !== 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' && (
        <>
          <Text fontSize='xs' color={useColorModeValue('gray.500', 'gray.500')}>
            {t('verify.codeSentMessage')}
          </Text>
          <Text fontSize='xs' color={useColorModeValue('gray.500', 'gray.500')}>
            {}
            <Link onClick={handleResendCode}>{t('verify.didNotReceive')}</Link>
          </Text>
        </>
      )}

      {type === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE' && (
        <Button variant='ghost' onClick={handleTotpCancel}>
          {t('verify.cancel')}
        </Button>
      )}
      <Toaster />
    </VStack>
  );
};

export default VerifyCodeForm;
